import React, { FC, memo, useCallback, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { MultipleSelect } from 'shared/ui/MultipleSelect';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { ZoneObjectType } from 'entities/Zone';
import { useGetPaginatedBoxListingQuery, BoxForListing } from 'entities/Box';
import { useGetWarehouseByIdQuery } from 'entities/Warehouse';
import { ZonesEditKeys } from '../model/types';

interface ObjectsSelectProps {
  warehouseId?: string;
  value: string[];
  onChange: (value: string[]) => void;
  onClear: (filterKey: string) => void;
}

export const ObjectsSelect: FC<ObjectsSelectProps> = memo((props) => {
  const { warehouseId, value, onChange, onClear } = props;

  const { t } = useAppTranslation('contracts');

  const { data: boxesResp } = useGetPaginatedBoxListingQuery({ warehouseId, limit: 1000 }, { skip: !warehouseId });
  const { data: warehouse } = useGetWarehouseByIdQuery({ warehouseId, params: { internalEquipment: true } }, { skip: !warehouseId });

  const options = useMemo((): Array<{ label: string; value: string }> => {
    if (!warehouseId) {
      return [];
    }

    const boxOptions = (boxesResp?.content ?? []).map((box: BoxForListing) => ({
      label: box.name,
      value: `${ZoneObjectType.BOX}:${box.boxId}`,
    }));

    const doors = warehouse?.internalEquipments || [];

    const doorOptions = doors.map((door) => ({
      label: getLocalizedString(door.name),
      value: `${ZoneObjectType.DOOR}:${door.internalEquipmentId}`,
    }));

    return [...doorOptions, ...boxOptions];
  }, [boxesResp?.content, warehouseId, warehouse?.internalEquipments]);

  const clearFilter = useCallback(() => {
    onClear(ZonesEditKeys.objectIds || 'objectIds');
  }, [onClear]);

  const handleChange = useCallback(
    (nextValues: string[]): void => {
      if (!nextValues.length) {
        clearFilter();
        return;
      }

      onChange(nextValues);
    },
    [onChange, clearFilter],
  );

  return (
    <MultipleSelect
      containerClassName="flex-1"
      className="w-full"
      label={t('Objects')}
      placeholder={t('All')}
      bordered
      allowClear
      options={options}
      value={value}
      onChange={handleChange}
      onClear={clearFilter}
      filterOption={(input: string, option) =>
        String(option?.label || '')
          .toLowerCase()
          .includes(input.toLowerCase())
      }
    />
  );
});
